import axios from 'axios';
import type {
  ApiError,
  CanvasContextResponse,
  CanvasContextSummary,
  CanvasEvent,
  ChatHistoryResponse,
  ChatMessageResponse,
  ChatStartResponse,
  HealthCheckResponse,
  InterviewSession,
  KnowledgeQueryRequest,
  KnowledgeQueryResponse,
} from '@shared/types.ts';

export interface GetSessionResponse {
  session: InterviewSession;
}

export type AssistantMode = 'dashboard' | 'custom';

export interface StartInterviewCall {
  id: string;
  assistantId?: string;
  status?: string;
  webCallUrl?: string;
  [key: string]: unknown;
}

export interface StartInterviewResponse {
  sessionId: string;
  assistantId: string;
  assistantMode?: AssistantMode;
  call: StartInterviewCall;
}

export interface EndInterviewResponse {
  sessionId: string;
  status: string;
  call: StartInterviewCall;
}

export interface AbortStartInterviewResponse {
  sessionId: string;
  status: string;
  reverted: boolean;
}

export interface InterviewerOverview {
  displayName: string;
  assistantId: string | null;
  source: 'vapi-assistant' | 'guide-preview' | 'custom-preview';
  mode: AssistantMode;
  systemPrompt: string;
  firstMessage?: string;
  model: {
    provider?: string;
    model?: string;
    temperature?: number;
  };
  voice: {
    provider?: string;
    voiceId?: string;
  };
  transcriber: {
    provider?: string;
    model?: string;
    language?: string;
  };
  input: {
    type: 'microphone';
    language?: string;
  };
  metadata: {
    sessionName: string;
    structureName?: string;
    intervieweeName?: string;
  };
  settings: Record<string, unknown>;
  raw?: Record<string, unknown>;
}

export interface InterviewerOverviewResponse {
  sessionId: string;
  interviewer: InterviewerOverview;
}

export interface TranscriptRecord {
  id: string;
  sessionId: string;
  content: unknown;
  rawText: string;
  createdAt: string;
  updatedAt?: string;
}

export interface GetTranscriptResponse {
  sessionId: string;
  transcript: TranscriptRecord;
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function formatApiError(error: unknown): Error {
  if (axios.isAxiosError<ApiError>(error)) {
    const statusCode = error.response?.status;
    const payload = error.response?.data;
    const message = payload?.error || error.message || 'Request failed';
    const statusText = statusCode ? ` (${statusCode})` : '';
    return new Error(`${message}${statusText}`);
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error('Unexpected API error');
}

export async function getSession(sessionId: string): Promise<GetSessionResponse> {
  try {
    const response = await apiClient.get<GetSessionResponse>(
      `/interview/session/${sessionId}`
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function getSessionByInvitation(invitationId: string): Promise<GetSessionResponse> {
  try {
    const response = await apiClient.get<GetSessionResponse>(
      `/interview/session/by-invitation/${invitationId}`
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function healthCheck(): Promise<HealthCheckResponse> {
  try {
    const response = await apiClient.get<HealthCheckResponse>('/health');
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export interface CustomConfig {
  modelProvider?: string;
  modelName?: string;
  temperature?: number;
  voiceProvider?: string;
  voiceId?: string;
  transcriberProvider?: string;
  transcriberModel?: string;
  transcriberLanguage?: string;
  firstMessage?: string;
}

export async function startInterview(
  sessionId: string,
  assistantMode: AssistantMode = 'dashboard',
  customConfig?: CustomConfig
): Promise<StartInterviewResponse> {
  try {
    const response = await apiClient.post<StartInterviewResponse>('/interview/start', {
      sessionId,
      assistantMode,
      customConfig,
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function endInterview(
  sessionId: string,
  transcript?: { role: string; text: string; timestamp?: string }[]
): Promise<EndInterviewResponse> {
  try {
    const response = await apiClient.post<EndInterviewResponse>('/interview/end', {
      sessionId,
      transcript,
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export interface RestartInterviewResponse {
  sessionId: string;
  status: string;
}

export async function restartInterview(sessionId: string): Promise<RestartInterviewResponse> {
  try {
    const response = await apiClient.post<RestartInterviewResponse>('/interview/restart', {
      sessionId,
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function abortInterviewStart(
  sessionId: string,
  reason?: string
): Promise<AbortStartInterviewResponse> {
  try {
    const response = await apiClient.post<AbortStartInterviewResponse>(
      '/interview/abort-start',
      {
        sessionId,
        reason,
      }
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function getTranscript(
  sessionId: string
): Promise<GetTranscriptResponse> {
  try {
    const response = await apiClient.get<GetTranscriptResponse>(
      `/interview/transcript/${sessionId}`
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function getInterviewerOverview(
  sessionId: string,
  assistantMode: AssistantMode = 'dashboard'
): Promise<InterviewerOverviewResponse> {
  try {
    const response = await apiClient.get<InterviewerOverviewResponse>(
      `/interview/agent/${sessionId}`,
      {
        params: { mode: assistantMode },
      }
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

// ─── Knowledge Query API ───────────────────────────────────────────────────

// ─── Chat Interview API (FEAT-9) ──────────────────────────────────────────

/**
 * Start an async chat interview. Transitions the session to IN_PROGRESS
 * and returns the AI interviewer's opening greeting.
 */
export async function startChatInterview(
  sessionId: string,
  options: { language?: string; coordinatorModel?: string } = {}
): Promise<ChatStartResponse> {
  try {
    const response = await apiClient.post<ChatStartResponse>(
      '/interview/chat/start',
      {
        sessionId,
        mode: 'ASYNC_CHAT',
        language: options.language ?? 'de',
        coordinatorModel: options.coordinatorModel,
      },
      { timeout: 60_000 } // Higher timeout for greeting generation
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

/**
 * Send a chat message and receive the AI interviewer's response.
 */
export async function sendChatMessage(
  sessionId: string,
  message: string,
  bridgePayload?: {
    canvasEvents?: CanvasEvent[];
    canvasContext?: CanvasContextSummary;
  },
): Promise<ChatMessageResponse> {
  try {
    const response = await apiClient.post<ChatMessageResponse>(
      '/interview/chat/message',
      { sessionId, message, ...bridgePayload },
      { timeout: 60_000 } // Agent processing can take up to 30s
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

/**
 * Fetch the full chat history for a session (page refresh recovery).
 */
export async function getChatHistory(
  sessionId: string
): Promise<ChatHistoryResponse> {
  try {
    const response = await apiClient.get<ChatHistoryResponse>(
      `/interview/chat/history/${sessionId}`
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

/**
 * Manually end a chat interview early.
 */
export async function endChatInterview(
  sessionId: string
): Promise<{ sessionId: string; status: string; duration?: number }> {
  try {
    const response = await apiClient.post<{ sessionId: string; status: string; duration?: number }>(
      '/interview/chat/end',
      { sessionId },
      { timeout: 30_000 }
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

/**
 * Trigger knowledge entity extraction for a completed session.
 * This runs as a dedicated request with its own serverless timeout.
 */
export async function triggerExtraction(
  sessionId: string
): Promise<{ sessionId: string; status: string }> {
  try {
    const response = await apiClient.post<{ sessionId: string; status: string }>(
      '/interview/chat/extract',
      { sessionId },
      { timeout: 120_000 } // Extraction can take up to 60s on serverless
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

// ─── Knowledge Query API ───────────────────────────────────────────────────

export async function queryKnowledge(
  request: KnowledgeQueryRequest
): Promise<KnowledgeQueryResponse> {
  try {
    const response = await apiClient.post<KnowledgeQueryResponse>(
      '/knowledge/query',
      request,
      { timeout: 45_000 } // Knowledge queries chain multiple LLM calls
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

// ─── Canvas API (FEAT-11) ─────────────────────────────────────────────────

/**
 * Load the initial canvas context for a chat interview session.
 * Returns persisted canvasState (if any) and preloaded knowledge entities.
 */
export async function getCanvasContext(
  sessionId: string
): Promise<CanvasContextResponse> {
  try {
    const response = await apiClient.get<CanvasContextResponse>(
      `/interview/chat/canvas/${sessionId}`,
      { timeout: 30_000 }
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

export async function saveCanvasState(
  sessionId: string,
  canvasState: unknown,
): Promise<void> {
  try {
    await apiClient.put(
      `/interview/chat/canvas/${sessionId}`,
      { canvasState },
      { timeout: 10_000 }
    );
  } catch (error) {
    // Non-critical — auto-save failure shouldn't disrupt the interview
    console.warn('[api] Canvas auto-save failed:', error);
  }
}

export async function sendNodeContextMessage(
  sessionId: string,
  nodeId: string,
  nodeLabel: string,
  nodeType: string,
  message: string,
  kind: NodeContextKind | undefined,
  canvasSnapshot?: {
    nodes: Array<{ id: string; type: string; name: string; description?: string }>;
    edges: Array<{ id: string; type: string; sourceId: string; targetId: string }>;
  },
  bridgePayload?: {
    canvasEvents?: CanvasEvent[];
    canvasContext?: CanvasContextSummary;
  },
): Promise<ChatMessageResponse> {
  try {
    const response = await apiClient.post<ChatMessageResponse>(
      '/interview/chat/node-context',
      { sessionId, nodeId, nodeLabel, nodeType, message, kind, canvasSnapshot, ...bridgePayload },
      { timeout: 30_000 }
    );
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
}

// ─── Node Context API (Track C.2) ───────────────────────────────────────────

export type NodeContextKind = 'detail' | 'exception' | 'correction';

export interface NodeContextRow {
  id: string;
  sessionId: string;
  nodeId: string;
  kind: NodeContextKind;
  text: string;
  createdAt: string;
}

/**
 * Persist a single AddContextSheet entry. Fire-and-forget from the caller's
 * perspective — local canvas state is the source of truth for UX, this write
 * just guarantees durability across reloads.
 */
export async function createNodeContext(
  sessionId: string,
  nodeId: string,
  kind: NodeContextKind,
  text: string,
): Promise<NodeContextRow> {
  try {
    const response = await apiClient.post<{ nodeContext: NodeContextRow }>(
      `/interview/${sessionId}/node-contexts`,
      { nodeId, kind, text },
      { timeout: 10_000 },
    );
    return response.data.nodeContext;
  } catch (error) {
    throw formatApiError(error);
  }
}

/** Hydrate node-context badges from the server on session load. */
export async function listNodeContexts(sessionId: string): Promise<NodeContextRow[]> {
  try {
    const response = await apiClient.get<{ nodeContexts: NodeContextRow[] }>(
      `/interview/${sessionId}/node-contexts`,
      { timeout: 10_000 },
    );
    return response.data.nodeContexts ?? [];
  } catch (error) {
    throw formatApiError(error);
  }
}

// ─── Canvas Event Log API (Track C.3) ───────────────────────────────────────

export interface CanvasEventRow {
  id: string;
  sessionId: string;
  turnIndex: number;
  /** Raw CanvasDelta payload — fed back through useProcessCanvas.applyDelta on replay. */
  delta: { nodes: unknown[]; edges: unknown[] };
  createdAt: string;
}

/**
 * Fetch the full canvas event log for a session, ordered chronologically.
 * Used by the Timeline Scrubber to rewind the canvas to any prior turn.
 */
export async function listCanvasEvents(sessionId: string): Promise<CanvasEventRow[]> {
  try {
    const response = await apiClient.get<{ canvasEvents: CanvasEventRow[] }>(
      `/interview/${sessionId}/canvas-events`,
      { timeout: 10_000 },
    );
    return response.data.canvasEvents ?? [];
  } catch (error) {
    throw formatApiError(error);
  }
}

// ============================================================================
// Debug API (development only)
// ============================================================================

export interface DebugInfo {
  session: {
    id: string;
    status: string;
    structureId: string;
    structureName: string;
    intervieweeName: string;
    mode: string;
    language: string;
    coordinatorModel: string | null;
    startedAt: string | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
  canvasState: {
    raw: unknown;
    summary: {
      nodeCount: number;
      nodesByType: Record<string, number>;
      edgeCount: number;
      stagedCount: number;
      dismissedCount: number;
      clusterCount: number;
      lastUpdatedAt: string | null;
    };
  };
  lastAgentRun: {
    messageId: string;
    timestamp: string;
    signals: Record<string, unknown> | null;
    model: string | null;
    isComplete: boolean;
    reflection: Record<string, unknown> | null;
  } | null;
  messageHistory: {
    total: number;
    intervieweeCount: number;
    interviewerCount: number;
    recent: Array<{
      id: string;
      role: string;
      contentPreview: string;
      fullContent: string;
      hasMetadata: boolean;
      timestamp: string;
    }>;
  };
}

export async function getDebugInfo(sessionId: string): Promise<DebugInfo> {
  const response = await apiClient.get<DebugInfo>(
    `/debug/${sessionId}`,
    { timeout: 10_000 },
  );
  return response.data;
}

